/**
 * SPA Navigation Logic
 * Handles client-side navigation for the Wardrob sneaker discovery platform
 * Implements history.pushState for seamless page transitions
 */

(function() {
  'use strict';

  // Store current state
  const state = {
    currentPath: window.location.pathname,
    isNavigating: false,
    previousCollection: null
  };

  // Cache DOM elements
  const elements = {
    mainPanel: null,
    sidebar: null,
    sidebarContent: null
  };

  /**
   * Build mobile retailer data from sidebar HTML retailer rows.
   * Extracts region, filters by visibility, detects active region.
   */
  function buildMobileRetailerData(sidebarRetailers) {
    var retailers = [];
    var activeRegion = 'US';
    var firstUrl = '';
    var firstName = '';

    sidebarRetailers.forEach(function(row) {
      var region = row.dataset.variantRegion || 'US';
      var name = row.dataset.retailerName || '';
      var price = row.dataset.variantPrice || '';
      var url = row.dataset.retailerUrl || '';
      var isHidden = row.style.display === 'none';

      retailers.push({ region: region, name: name, price: price, url: url, hidden: isHidden });

      // First visible row determines active region and first retailer
      if (!isHidden && !firstUrl) {
        activeRegion = region;
        firstUrl = url;
        firstName = name;
      }
    });

    return { retailers: retailers, activeRegion: activeRegion, firstUrl: firstUrl, firstName: firstName };
  }

  /**
   * Build mobile retailers HTML — only show rows matching active region.
   * Each row gets data-variant-region for client-side filtering.
   */
  function buildMobileRetailersHtml(data) {
    if (data.retailers.length === 0) return '';
    var html = '';
    var isFirst = true;
    var visibleCount = 0;

    data.retailers.forEach(function(r) {
      var show = visibleCount < 2 && (r.region === data.activeRegion || r.region === 'INT');
      var initials = r.name.substring(0, 2).toUpperCase();
      var priceDisplay = r.price === 'MARKETPLACE'
        ? '<span class="retailer-row__badge">MARKETPLACE</span>'
        : (r.price ? '<span class="retailer-row__price">' + r.price + '</span>' : '');

      html += '<div class="retailer-row' + (show && isFirst ? ' retailer-row--active' : '') + '"'
        + ' data-variant-region="' + r.region + '"'
        + ' data-retailer-url="' + r.url + '"'
        + ' data-retailer-name="' + r.name + '"'
        + (show ? '' : ' style="display:none"')
        + '>'
        + '<div class="retailer-row__logo"><span>' + initials + '</span></div>'
        + '<span class="retailer-row__name">' + r.name + '</span>'
        + priceDisplay
        + '</div>';

      if (show) { isFirst = false; visibleCount++; }
    });

    return html;
  }

  /**
   * Build mobile region flags HTML with correct active state.
   */
  function buildMobileFlagsHtml(activeRegion) {
    var flags = [
      { code: 'UK', emoji: '🇬🇧' },
      { code: 'US', emoji: '🇺🇸' },
      { code: 'EU', emoji: '🇪🇺' },
      { code: 'INT', emoji: '🌐' }
    ];
    var html = '';
    flags.forEach(function(f) {
      var active = f.code === activeRegion ? ' region-flag--active' : '';
      html += '<button type="button" class="region-flag' + active + '" data-mobile-region="' + f.code + '">' + f.emoji + '</button>';
    });
    html += '<span class="region-info">ⓘ</span>';
    return html;
  }

  /**
   * Build curated collection tag chips (Gender + Editorial + Type).
   */
  function buildMobileTagsHtml(tags, productType) {
    var html = '<div class="mobile-product-details__tags" data-mobile-tags>';
    var tagList = typeof tags === 'string' ? tags.split(', ') : tags || [];
    tagList.forEach(function(tag) {
      var tl = tag.toLowerCase();
      if (tl === 'mens') html += '<a href="/collections/mens" class="tag-chip tag-chip--small">MENS</a>';
      else if (tl === 'womens') html += '<a href="/collections/womens" class="tag-chip tag-chip--small">WOMENS</a>';
      else if (tl === 'unisex') html += '<a href="/collections/unisex" class="tag-chip tag-chip--small">UNISEX</a>';
      if (tag.indexOf('editorial:') === 0) {
        var name = tag.replace('editorial:', '').replace(/-/g, ' ').toUpperCase();
        var handle = tag.replace('editorial:', '');
        html += '<a href="/collections/' + handle + '" class="tag-chip tag-chip--small tag-chip--editorial">#' + name + '</a>';
      }
    });
    if (productType) {
      var typeHandle = productType.toLowerCase().replace(/\s+/g, '-');
      html += '<a href="/collections/' + typeHandle + '" class="tag-chip tag-chip--small">' + productType.toUpperCase() + '</a>';
    }
    html += '</div>';
    return html;
  }

  /**
   * Initialize the SPA navigation
   */
  function init() {
    // Cache elements
    elements.mainPanel = document.querySelector('[data-main-panel]');
    elements.sidebar = document.querySelector('[data-sidebar]');
    elements.sidebarContent = document.querySelector('[data-sidebar-content]');

    if (!elements.mainPanel || !elements.sidebar) {
      console.warn('SPA: Required elements not found');
      return;
    }

    // Bind event listeners
    bindNavigationLinks();
    bindPopState();

    // Mobile region switcher (delegated — mobile content is dynamic)
    document.addEventListener('click', function(e) {
      var flag = e.target.closest('[data-mobile-region]');
      if (!flag) return;

      var region = flag.dataset.mobileRegion;
      var container = flag.closest('.mobile-product-details__bottom-panel') || flag.closest('[data-mobile-bottom]');
      if (!container) return;

      // Update active flag
      container.querySelectorAll('[data-mobile-region]').forEach(function(f) {
        f.classList.toggle('region-flag--active', f.dataset.mobileRegion === region);
      });

      // INT shows US direct variants + INT marketplace; UK/US/EU show their own + INT marketplace
      // Max 2 visible per region
      var showRegion = region === 'INT' ? 'US' : region;
      var rows = container.querySelectorAll('[data-variant-region]');
      var firstVisible = true;
      var visibleCount = 0;
      var firstUrl = '';
      var firstName = '';

      rows.forEach(function(row) {
        var rowRegion = row.dataset.variantRegion;
        var show = visibleCount < 2 && (rowRegion === showRegion || rowRegion === 'INT');
        row.style.display = show ? '' : 'none';
        row.classList.remove('retailer-row--active');
        if (show) {
          visibleCount++;
          if (firstVisible) {
            row.classList.add('retailer-row--active');
            firstUrl = row.dataset.retailerUrl || '';
            firstName = row.dataset.retailerName || '';
            firstVisible = false;
          }
        }
      });

      // Update shop button
      var shopBtn = container.querySelector('[data-shop-btn]');
      if (shopBtn && firstUrl) {
        shopBtn.href = firstUrl;
        shopBtn.textContent = 'Shop from ' + firstName;
      }
    });

    // Store initial state
    history.replaceState(
      { path: state.currentPath, type: getPageType(state.currentPath) },
      '',
      state.currentPath
    );


  }

  /**
   * Determine page type from path
   */
  function getPageType(path) {
    if (path === '/' || path === '') return 'index';
    if (path.startsWith('/collections/')) return 'collection';
    if (path.startsWith('/products/')) return 'product';
    if (path.startsWith('/blogs/') && path.split('/').filter(Boolean).length >= 3) return 'article';
    if (path.startsWith('/blogs/')) return 'blog';
    if (path.startsWith('/pages/')) return 'page';
    if (path.startsWith('/search')) return 'search';
    return 'other';
  }

  /**
   * Extract handle from path
   */
  function getHandle(path) {
    const parts = path.split('/').filter(Boolean);
    return parts[parts.length - 1] || null;
  }

  /**
   * Bind click handlers to navigation links
   */
  function bindNavigationLinks() {
    document.addEventListener('click', function(e) {
      const link = e.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href) return;

      // Skip external links, anchors, and special links
      if (href.startsWith('http') && !href.includes(window.location.host)) return;
      if (href.startsWith('#')) return;
      if (href.startsWith('mailto:') || href.startsWith('tel:')) return;
      if (link.hasAttribute('target') && link.getAttribute('target') === '_blank') return;
      if (link.hasAttribute('data-no-spa')) return;

      // Skip blocked routes
      const blockedPaths = ['/cart', '/checkout', '/account'];
      if (blockedPaths.some(blocked => href.startsWith(blocked))) {
        e.preventDefault();
        console.warn('SPA: Navigation to cart/checkout blocked');
        return;
      }

      // Handle SPA navigation
      const pageType = getPageType(href);
      if (['index', 'collection', 'product', 'search', 'blog', 'article'].includes(pageType)) {
        e.preventDefault();
        navigateTo(href, pageType);
      }
    });
  }

  /**
   * Handle browser back/forward navigation
   */
  function bindPopState() {
    window.addEventListener('popstate', function(e) {
      if (e.state && e.state.path) {
        navigateTo(e.state.path, e.state.type, false);
      }
    });
  }

  /**
   * Update body template class for CSS layout rules (critical for mobile).
   * Mobile CSS uses .template-index, .template-collection, .template-product etc.
   * to show/hide panels. SPA must keep these in sync.
   */
  function updateBodyClass(pageType) {
    // Remove all existing template-* classes
    const classes = document.body.className.split(/\s+/);
    classes.forEach(function(cls) {
      if (cls.startsWith('template-')) {
        document.body.classList.remove(cls);
      }
    });

    // Map page type to Shopify template class
    const templateMap = {
      'index': 'template-index',
      'collection': 'template-collection',
      'product': 'template-product',
      'blog': 'template-blog',
      'article': 'template-article',
      'search': 'template-search',
      'page': 'template-page'
    };

    const templateClass = templateMap[pageType] || 'template-page';
    document.body.classList.add(templateClass);
  }

  /**
   * Navigate to a new page via SPA
   */
  async function navigateTo(path, type, pushState = true) {
    if (state.isNavigating) return;
    if (path === state.currentPath) return;

    state.isNavigating = true;

    try {
      // Add loading state
      document.body.classList.add('is-navigating');

      // Update body template class for CSS layout rules (critical for mobile)
      updateBodyClass(type);

      // Determine what needs to be updated
      const currentType = getPageType(state.currentPath);

      if (type === 'product' && currentType === 'collection') {
        // Product from collection: update sidebar and show product view
        await updateProductPanel(path);
        state.previousCollection = state.currentPath;
      } else if (type === 'product' && currentType === 'index') {
        // Product from homepage: clean up video player, then show product
        cleanupVideoPlayer();
        await updateProductPanel(path);
        state.previousCollection = state.currentPath;
      } else if (type === 'product' && currentType === 'product') {
        // Product to product: use same reliable flow as collection→product
        await updateProductPanel(path);
      } else if (type === 'collection' && currentType === 'product') {
        // Back from product: restore previous page (collection or homepage)
        const previousPath = state.previousCollection || path;
        const previousType = getPageType(previousPath);
        await updateFullPage(previousPath, previousType);
        state.previousCollection = null;
      } else if (type === 'index' && currentType === 'product') {
        // Explicit navigation back to homepage from product
        await updateFullPage(path, type);
        state.previousCollection = null;
      } else {
        // Full page transition
        await updateFullPage(path, type);
      }

      // Update URL
      if (pushState) {
        history.pushState({ path, type }, '', path);
      }

      state.currentPath = path;

      // Rebind navigation links for new content
      bindDynamicContent();

    } catch (error) {
      console.error('SPA: Navigation failed', error);
      // Fallback to traditional navigation
      window.location.href = path;
    } finally {
      state.isNavigating = false;
      document.body.classList.remove('is-navigating');
    }
  }

  /**
   * Update only the product panel (sidebar) when clicking a product
   */
  async function updateProductPanel(path) {
    const html = await fetchSection(path, 'sidebar-product');

    if (elements.sidebarContent) {
      // Animate out
      elements.sidebarContent.style.opacity = '0';
      elements.sidebarContent.style.transform = 'translateX(20px)';

      await delay(150);

      elements.sidebarContent.innerHTML = html;
      executeScripts(elements.sidebarContent);

      // Animate in
      requestAnimationFrame(() => {
        elements.sidebarContent.style.opacity = '1';
        elements.sidebarContent.style.transform = 'translateX(0)';
      });

      // Update main panel to show product hero
      await updateProductHero(path);
    }
  }

  /**
   * Update the product view with gallery and collection thumbnails
   */
  async function updateProductHero(path) {
    const productHandle = getHandle(path);
    if (!productHandle) return;

    // Fetch product data AND sidebar section (for retailers) in parallel
    try {
      const [productResponse, sidebarHtml] = await Promise.all([
        fetch(`${path}.json`),
        fetchSection(path, 'sidebar-product')
      ]);
      const data = await productResponse.json();
      const product = data.product;

      // Extract retailers from sidebar HTML
      const parser = new DOMParser();
      const sidebarDoc = parser.parseFromString(sidebarHtml, 'text/html');
      const sidebarRetailers = sidebarDoc.querySelectorAll('[data-retailer-row]');

      // Build retailers HTML from sidebar data (region-aware)
      var retailerData;
      let retailersHtml = '';
      let firstRetailerUrl = product.url || path;
      let firstRetailerName = product.vendor || '';

      if (sidebarRetailers.length > 0) {
        retailerData = buildMobileRetailerData(sidebarRetailers);
        retailersHtml = buildMobileRetailersHtml(retailerData);
        firstRetailerUrl = retailerData.firstUrl || firstRetailerUrl;
        firstRetailerName = retailerData.firstName || firstRetailerName;
      } else {
        retailerData = { activeRegion: 'US' };
        const vendorInitials = (product.vendor || '').substring(0, 2).toUpperCase();
        const fallbackPrice = product.variants && product.variants[0] ? '$' + (product.variants[0].price / 100).toFixed(2) : '';
        retailersHtml = `
          <div class="retailer-row retailer-row--active" data-retailer-url="${product.url || path}">
            <div class="retailer-row__logo"><span>${vendorInitials}</span></div>
            <span class="retailer-row__name">${product.vendor || ''}</span>
            <span class="retailer-row__price">${fallbackPrice}</span>
          </div>
        `;
      }

      if (product && product.images && product.images.length > 0) {
        // Build gallery HTML with all product images
        let galleryHtml = '';
        product.images.forEach((image, index) => {
          const imageUrl = image.src || image;
          galleryHtml += `
            <div class="product-gallery__item${index === 0 ? ' product-gallery__item--main' : ''}">
              <img
                src="${imageUrl}"
                alt="${product.title}"
                class="product-gallery__image"
                loading="${index === 0 ? 'eager' : 'lazy'}"
              >
            </div>
          `;
        });

        // Get collection thumbnails from existing grid or fetch
        let collectionHtml = '';
        const existingCards = document.querySelectorAll('.product-card');
        if (existingCards.length > 0) {
          existingCards.forEach(card => {
            const cardHandle = card.dataset.productHandle;
            const cardImg = card.querySelector('.product-card__image img');
            const cardBrand = card.querySelector('.product-card__brand');
            const cardTitle = card.querySelector('.product-card__title');
            const cardPrice = card.querySelector('.product-card__price');
            const isActive = cardHandle === productHandle;

            collectionHtml += `
              <a href="/products/${cardHandle}" class="collection-thumb${isActive ? ' collection-thumb--active' : ''}" data-spa-link>
                ${cardImg ? `<img src="${cardImg.src}" alt="${cardHandle}" loading="lazy">` : ''}
                <div class="collection-thumb__info">
                  <span class="collection-thumb__brand">${cardBrand ? cardBrand.textContent.trim() : ''}</span>
                  <span class="collection-thumb__title">${cardTitle ? cardTitle.textContent.trim() : ''}</span>
                  <span class="collection-thumb__price">${cardPrice ? cardPrice.textContent.trim() : ''}</span>
                </div>
              </a>
            `;
          });
        }

        // Build mobile product details HTML - New fixed bottom panel layout
        // Use product.vendor for brand display, firstRetailerName for Shop button
        const productDesc = product.body_html || `<p>The ${(product.vendor || '')} ${(product.title || '').split(' ')[0]} features premium craftsmanship and timeless design.</p>`;

        // Editorial content (scrolls after images)
        const editorialHtml = `
          <div class="mobile-product-details__editorial" data-mobile-editorial>
            <h1 class="mobile-product-details__full-title">
              <span class="mobile-product-details__vendor">${product.vendor || ''}</span> - ${product.title || ''}
            </h1>
            <div class="mobile-product-details__description">
              ${productDesc}
            </div>
          </div>
        `;

        // Fixed header
        const headerHtml = `
          <div class="mobile-product-details__header" data-mobile-header>
            <button type="button" class="mobile-product-details__back" data-mobile-back aria-label="Back">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="19" y1="12" x2="5" y2="12"/>
                <polyline points="12 19 5 12 12 5"/>
              </svg>
            </button>
            <span class="mobile-product-details__brand">${product.vendor || ''}</span>
            <span class="mobile-product-details__title">${(product.title || '').substring(0, 25)}</span>
            <button type="button" class="mobile-product-details__close" data-mobile-close aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        `;

        // Fixed tags row
        const tagsHtml = buildMobileTagsHtml(product.tags, product.product_type);

        // Fixed bottom panel with retailers (from sidebar data)
        const bottomPanelHtml = `
          <div class="mobile-product-details__bottom-panel" data-mobile-bottom>
            <div class="mobile-product-details__flags">
              ${buildMobileFlagsHtml(retailerData ? retailerData.activeRegion : 'US')}
            </div>
            <div class="mobile-product-details__retailers" data-mobile-retailers>
              ${retailersHtml}
            </div>
            <div class="mobile-product-details__footer">
              <button type="button" class="btn-add" data-wishlist-add="${product.handle}" data-product-id="${product.id}">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="16"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                Add
              </button>
              <a href="${firstRetailerUrl}" class="btn-shop" target="_blank" rel="noopener" data-shop-btn>
                Shop from ${firstRetailerName}
              </a>
            </div>
          </div>
        `;

        const mobileDetailsHtml = editorialHtml + headerHtml + tagsHtml + bottomPanelHtml;

        // Build complete product view HTML
        const productViewHtml = `
          <div class="product-view" data-product-view>
            <div class="product-view__collection-col" data-collection-thumbnails>
              ${collectionHtml}
            </div>
            <div class="product-view__gallery" data-product-gallery>
              ${galleryHtml}
              ${mobileDetailsHtml}
            </div>
          </div>
        `;

        // Replace main panel content
        elements.mainPanel.innerHTML = productViewHtml;

        // Scroll gallery to top
        const gallery = document.querySelector('[data-product-gallery]');
        if (gallery) {
          gallery.scrollTop = 0;
        }
      }
    } catch (error) {
      console.warn('SPA: Could not fetch product data for gallery', error);
    }
  }

  /**
   * Update from one product to another product (sidebar + gallery + thumbnails)
   */
  async function updateProductToProduct(path) {
    const productHandle = getHandle(path);
    if (!productHandle) return;

    // Fetch sidebar and product data in parallel
    const [sidebarHtml, productResponse] = await Promise.all([
      fetchSection(path, 'sidebar-product'),
      fetch(`${path}.json`)
    ]);

    // Update sidebar
    if (elements.sidebarContent) {
      elements.sidebarContent.style.opacity = '0';
      await delay(100);
      elements.sidebarContent.innerHTML = sidebarHtml;
      executeScripts(elements.sidebarContent);
      elements.sidebarContent.style.opacity = '1';
    }

    // Extract retailers from sidebar HTML
    const parser = new DOMParser();
    const sidebarDoc = parser.parseFromString(sidebarHtml, 'text/html');
    const sidebarRetailers = sidebarDoc.querySelectorAll('[data-retailer-row]');

    // Update gallery with new product images
    try {
      const data = await productResponse.json();
      const product = data.product;

      // Build retailers HTML from sidebar data (region-aware)
      var retailerData2;
      let retailersHtml = '';
      let firstRetailerUrl = product.url || path;
      let firstRetailerName = product.vendor || '';

      if (sidebarRetailers.length > 0) {
        retailerData2 = buildMobileRetailerData(sidebarRetailers);
        retailersHtml = buildMobileRetailersHtml(retailerData2);
        firstRetailerUrl = retailerData2.firstUrl || firstRetailerUrl;
        firstRetailerName = retailerData2.firstName || firstRetailerName;
      } else {
        retailerData2 = { activeRegion: 'US' };
        const vendorInitials = (product.vendor || '').substring(0, 2).toUpperCase();
        const fallbackPrice = product.variants && product.variants[0] ? '$' + (product.variants[0].price / 100).toFixed(2) : '';
        retailersHtml = `
          <div class="retailer-row retailer-row--active" data-retailer-url="${product.url || path}">
            <div class="retailer-row__logo"><span>${vendorInitials}</span></div>
            <span class="retailer-row__name">${product.vendor || ''}</span>
            <span class="retailer-row__price">${fallbackPrice}</span>
          </div>
        `;
      }

      if (product && product.images && product.images.length > 0) {
        // Build new gallery HTML
        let galleryHtml = '';
        product.images.forEach((image, index) => {
          const imageUrl = image.src || image;
          galleryHtml += `
            <div class="product-gallery__item${index === 0 ? ' product-gallery__item--main' : ''}">
              <img
                src="${imageUrl}"
                alt="${product.title}"
                class="product-gallery__image"
                loading="${index === 0 ? 'eager' : 'lazy'}"
              >
            </div>
          `;
        });

        // Build mobile product details HTML
        // Use product.vendor for brand display, firstRetailerName for Shop button
        const productDesc = product.body_html || `<p>The ${(product.vendor || '')} ${(product.title || '').split(' ')[0]} features premium craftsmanship and timeless design.</p>`;

        // Editorial content (scrolls after images)
        const editorialHtml = `
          <div class="mobile-product-details__editorial" data-mobile-editorial>
            <h1 class="mobile-product-details__full-title">
              <span class="mobile-product-details__vendor">${product.vendor || ''}</span> - ${product.title || ''}
            </h1>
            <div class="mobile-product-details__description">
              ${productDesc}
            </div>
          </div>
        `;

        // Fixed header
        const headerHtml = `
          <div class="mobile-product-details__header" data-mobile-header>
            <button type="button" class="mobile-product-details__back" data-mobile-back aria-label="Back">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="19" y1="12" x2="5" y2="12"/>
                <polyline points="12 19 5 12 12 5"/>
              </svg>
            </button>
            <span class="mobile-product-details__brand">${product.vendor || ''}</span>
            <span class="mobile-product-details__title">${(product.title || '').substring(0, 25)}</span>
            <button type="button" class="mobile-product-details__close" data-mobile-close aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        `;

        // Fixed tags row
        const tagsHtml = buildMobileTagsHtml(product.tags, product.product_type);

        // Fixed bottom panel with retailers (from sidebar data)
        const bottomPanelHtml = `
          <div class="mobile-product-details__bottom-panel" data-mobile-bottom>
            <div class="mobile-product-details__flags">
              ${buildMobileFlagsHtml(retailerData2 ? retailerData2.activeRegion : 'US')}
            </div>
            <div class="mobile-product-details__retailers" data-mobile-retailers>
              ${retailersHtml}
            </div>
            <div class="mobile-product-details__footer">
              <button type="button" class="btn-add" data-wishlist-add="${product.handle}" data-product-id="${product.id}">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="16"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                Add
              </button>
              <a href="${firstRetailerUrl}" class="btn-shop" target="_blank" rel="noopener" data-shop-btn>
                Shop from ${firstRetailerName}
              </a>
            </div>
          </div>
        `;

        const mobileDetailsHtml = editorialHtml + headerHtml + tagsHtml + bottomPanelHtml;

        // Update gallery content with mobile details
        const gallery = document.querySelector('[data-product-gallery]');
        if (gallery) {
          gallery.innerHTML = galleryHtml + mobileDetailsHtml;
          gallery.scrollTop = 0;
        }

        // Update active state on collection thumbnails
        const thumbs = document.querySelectorAll('.collection-thumb');
        thumbs.forEach(thumb => {
          const thumbHref = thumb.getAttribute('href');
          const thumbHandle = thumbHref ? thumbHref.split('/').pop() : '';
          if (thumbHandle === productHandle) {
            thumb.classList.add('collection-thumb--active');
          } else {
            thumb.classList.remove('collection-thumb--active');
          }
        });
      }
    } catch (error) {
      console.warn('SPA: Could not update product gallery', error);
    }
  }

  /**
   * Update sidebar content for collection navigation
   */
  async function updateSidebar(path) {
    const pageType = getPageType(path);
    let sectionId = 'sidebar-collection';

    if (pageType === 'index') sectionId = 'sidebar-homepage';
    else if (pageType === 'product') sectionId = 'sidebar-product';

    const html = await fetchSection(path, sectionId);

    if (elements.sidebarContent) {
      elements.sidebarContent.style.opacity = '0';
      await delay(150);
      elements.sidebarContent.innerHTML = html;
      executeScripts(elements.sidebarContent);
      elements.sidebarContent.style.opacity = '1';
    }
  }

  /**
   * Update entire page (both panels)
   */
  async function updateFullPage(path, type) {
    // Fetch main content and sidebar in parallel
    const [mainHtml, sidebarHtml] = await Promise.all([
      fetchMainContent(path, type),
      fetchSidebarContent(path, type)
    ]);

    // Update main panel
    if (elements.mainPanel && mainHtml) {
      elements.mainPanel.style.opacity = '0';
      await delay(150);
      elements.mainPanel.innerHTML = mainHtml;
      executeScripts(elements.mainPanel);
      elements.mainPanel.style.opacity = '1';
    }

    // Update sidebar
    if (elements.sidebarContent && sidebarHtml) {
      elements.sidebarContent.innerHTML = sidebarHtml;
      executeScripts(elements.sidebarContent);
    }

    // Scroll to top
    window.scrollTo(0, 0);
  }

  /**
   * Fetch main panel content based on page type
   */
  async function fetchMainContent(path, type) {
    let sectionId;

    switch (type) {
      case 'index':
        sectionId = 'video-player';
        break;
      case 'collection':
        sectionId = 'product-grid';
        break;
      case 'blog':
        sectionId = 'blog-content';
        break;
      case 'article':
        sectionId = 'article-content';
        break;
      case 'product':
        // Product pages show hero + collection continue
        return null; // Handle separately
      default:
        return null;
    }

    return fetchSection(path, sectionId);
  }

  /**
   * Fetch sidebar content based on page type
   */
  async function fetchSidebarContent(path, type) {
    let sectionId;

    switch (type) {
      case 'index':
        sectionId = 'sidebar-homepage';
        break;
      case 'collection':
        sectionId = 'sidebar-collection';
        break;
      case 'blog':
        sectionId = 'sidebar-blog';
        break;
      case 'article':
        sectionId = 'sidebar-article';
        break;
      case 'product':
        sectionId = 'sidebar-product';
        break;
      case 'search':
        sectionId = 'search-results';
        break;
      default:
        return null;
    }

    return fetchSection(path, sectionId);
  }

  /**
   * Fetch a section from Shopify Section Rendering API
   */
  async function fetchSection(path, sectionId) {
    const url = `${path}?section_id=${sectionId}`;

    const response = await fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch section: ${response.status}`);
    }

    return response.text();
  }

  /**
   * Rebind event handlers for dynamically loaded content
   */
  function bindDynamicContent() {
    // Update wishlist button states for new content (binding is handled once globally)
    if (window.Wishlist) {
      window.Wishlist.updateButtonStates();
    }

    // Rebind filter chips
    bindFilterChips();

    // Rebind back button in product panel
    bindProductPanelButtons();
  }

  /**
   * Bind filter chip click handlers
   */
  function bindFilterChips() {
    const chips = document.querySelectorAll('[data-filter-chip]');
    chips.forEach(chip => {
      chip.addEventListener('click', function() {
        const filter = this.dataset.filterChip;
        applyFilter(filter);
      });
    });
  }

  /**
   * Apply filter to product grid
   */
  function applyFilter(filter) {
    const cards = document.querySelectorAll('.product-card');
    const chips = document.querySelectorAll('[data-filter-chip]');

    // Update active chip
    chips.forEach(chip => {
      chip.classList.toggle('filter-chip--active', chip.dataset.filterChip === filter);
    });

    // Filter cards
    cards.forEach(card => {
      if (filter === 'all') {
        card.style.display = '';
        return;
      }

      const productType = card.dataset.productType?.toLowerCase() || '';
      const matches = productType.includes(filter.toLowerCase());
      card.style.display = matches ? '' : 'none';
    });
  }

  /**
   * Bind product panel button handlers
   */
  function bindProductPanelButtons() {
    // Back button (desktop sidebar)
    const backBtn = document.querySelector('[data-product-back]');
    if (backBtn) {
      backBtn.addEventListener('click', function(e) {
        e.preventDefault();
        if (state.previousCollection) {
          const backType = getPageType(state.previousCollection);
          navigateTo(state.previousCollection, backType);
        } else {
          history.back();
        }
      });
    }

    // Close button (desktop sidebar)
    const closeBtn = document.querySelector('[data-product-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function(e) {
        e.preventDefault();
        if (state.previousCollection) {
          const closeType = getPageType(state.previousCollection);
          navigateTo(state.previousCollection, closeType);
        } else {
          navigateTo('/collections/sneakers', 'collection');
        }
      });
    }

    // Mobile back button
    const mobileBackBtn = document.querySelector('[data-mobile-back]');
    if (mobileBackBtn) {
      mobileBackBtn.addEventListener('click', function(e) {
        e.preventDefault();
        if (state.previousCollection) {
          const backType = getPageType(state.previousCollection);
          navigateTo(state.previousCollection, backType);
        } else {
          history.back();
        }
      });
    }

    // Mobile close button
    const mobileCloseBtn = document.querySelector('[data-mobile-close]');
    if (mobileCloseBtn) {
      mobileCloseBtn.addEventListener('click', function(e) {
        e.preventDefault();
        if (state.previousCollection) {
          const closeType = getPageType(state.previousCollection);
          navigateTo(state.previousCollection, closeType);
        } else {
          navigateTo('/collections/sneakers', 'collection');
        }
      });
    }
  }

  /**
   * Clean up Wistia video player before replacing main panel
   */
  function cleanupVideoPlayer() {
    try {
      if (window.VideoPlayer && window.VideoPlayer.getPlayer()) {
        var p = window.VideoPlayer.getPlayer();
        p.pause();
        p.remove();
      }
      // Also remove any lingering Wistia embeds
      var wistiaEls = document.querySelectorAll('.wistia_embed, [id*="wistia"]');
      wistiaEls.forEach(function(el) {
        if (el.closest('[data-main-panel]') || el.closest('.main-panel')) {
          el.innerHTML = '';
        }
      });
    } catch(e) {
      console.warn('SPA: Video cleanup error', e);
    }
  }

  /**
   * Utility: delay for animations
   */
  function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Execute inline scripts after innerHTML injection.
   * Scripts added via innerHTML don't execute — this re-creates them.
   */
  function executeScripts(container) {
    if (!container) return;
    const scripts = container.querySelectorAll('script');
    scripts.forEach(function(oldScript) {
      const newScript = document.createElement('script');
      // Copy attributes
      Array.from(oldScript.attributes).forEach(function(attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  /**
   * Close product panel (mobile)
   */
  function closeProductPanel() {
    const sidebar = document.querySelector('.sidebar');
    if (sidebar) {
      sidebar.classList.remove('is-open');
    }
  }

  /**
   * Open product panel (mobile)
   */
  function openProductPanel() {
    const sidebar = document.querySelector('.sidebar');
    if (sidebar) {
      sidebar.classList.add('is-open');
    }
  }

  // Expose methods globally
  window.SPA = {
    navigateTo,
    closeProductPanel,
    openProductPanel
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
